import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Drawer,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Button,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import api from "../../api";

const TYPE_CONFIG = {
  p: {
    code: "PRG",
    iconBg: "#e4f1f8",
    iconFg: "#0172A6",
  },
  s: {
    code: "SUB",
    iconBg: "#f0ecfa",
    iconFg: "#6b3fa8",
  },
  o: {
    code: "MCQ",
    iconBg: "#e8f7ee",
    iconFg: "#1f7a45",
  },
  assignment: {
    code: "ASG",
    iconBg: "#fff3dd",
    iconFg: "#a5751a",
  },
};

const DIFFICULTY_CONFIG = {
  E: { label: "EASY", fg: "#1f7a45", bg: "#e8f7ee" },
  M: { label: "MEDIUM", fg: "#b07d1a", bg: "#fff3dd" },
  H: { label: "HARD", fg: "#c93c3c", bg: "#fbe3e3" },
};

const FILTERS = [
  { key: "all", label: "All types" },
  { key: "p", label: "Programming" },
  { key: "s", label: "Subjective" },
  { key: "o", label: "MCQ" },
];


const LibraryPickerDrawer = ({
  open,
  onClose,
  topicTemplateId,
  topicName,
  batchName,
  contentType,
  isBatch,
  studentCount,
  onAssignComplete,
  assignedIds = [],
}) => {
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [allItems, setAllItems] = useState([]); // Full unfiltered list
  const [selectedItems, setSelectedItems] = useState([]);

  // Fetch all library items once when drawer opens
  useEffect(() => {
    const fetchLibrary = async () => {
      if (!open) return;

      setLoading(true);
      try {
        // Determine which API to call based on content type
        let endpoint = "api/question-library/";
        if (contentType === "assignments") {
          endpoint = "api/assignment-library/";
        }

        const response = await api.get(endpoint);

        // Normalize the response data
        let items = [];
        if (contentType === "assessments") {
          items = response.data.questions || response.data || [];
        } else if (contentType === "assignments") {
          items = response.data.assignments || response.data || [];
        } else {
          items = response.data.materials || [];
        }

        // Ensure items is always an array
        if (!Array.isArray(items)) {
          items = [];
        }

        setAllItems(items);
      } catch (err) {
        console.error("Failed to fetch library:", err);
        setAllItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLibrary();
  }, [open, contentType]);

  // Filter items client-side
  const libraryItems = React.useMemo(() => {
    let filtered = allItems;

    // Type filter (for assessments)
    if (activeFilter !== "all" && contentType === "assessments") {
      filtered = filtered.filter((item) => item.type === activeFilter);
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.title?.toLowerCase().includes(query) ||
          item.description?.toLowerCase().includes(query) ||
          item.tags?.some((tag) => tag.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [allItems, activeFilter, searchQuery, contentType]);

  // Reset selection when drawer closes
  useEffect(() => {
    if (!open) {
      setSelectedItems([]);
      setSearchQuery("");
      setActiveFilter("all");
      setAllItems([]);
    }
  }, [open]);

  const handleToggleSelect = (item) => {
    const itemKey = `${item.type || contentType}-${item.id}`;
    if (assignedIds.includes(itemKey)) return; // Can't select already assigned items

    setSelectedItems((prev) => {
      const exists = prev.find((i) => i.id === item.id && i.type === item.type);
      if (exists) {
        return prev.filter((i) => !(i.id === item.id && i.type === item.type));
      }
      return [...prev, item];
    });
  };

  const isSelected = (item) => {
    return selectedItems.some((i) => i.id === item.id && i.type === item.type);
  };

  const isAssigned = (item) => {
    const itemKey = `${item.type || contentType}-${item.id}`;
    return assignedIds.includes(itemKey);
  };

  const handleAssign = async () => {
    if (selectedItems.length === 0 || !topicTemplateId) return;

    setAssigning(true);
    try {
      // Group items by type and call appropriate APIs
      const questions = selectedItems.filter((i) =>
        ["p", "s", "o"].includes(i.type)
      );
      const assignments = selectedItems.filter((i) => i.type === "assignment");

      if (questions.length > 0) {
        await api.post(`api/course-template/${topicTemplateId}/questions/assign/`, {
          questions: questions.map((q) => ({
            type: q.type,
            id: q.id,
          })),
        });
      }

      if (assignments.length > 0) {
        await api.post(`api/course-template/${topicTemplateId}/library-assignments/assign/`, {
          assignments: assignments.map((a) => a.id),
        });
      }

      onAssignComplete();
    } catch (err) {
      console.error("Failed to assign content:", err);
    } finally {
      setAssigning(false);
    }
  };

  const totalScore = selectedItems.reduce((acc, item) => acc + (item.max_score || 0), 0);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 560, display: "flex", flexDirection: "column" },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: "20px 24px",
          borderBottom: "1px solid #e4eaf0",
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: "17px", fontWeight: 700 }}>
            Add from library
          </Typography>
          <Typography sx={{ fontSize: "13px", color: "#66808f" }}>
            Assign to <strong style={{ color: "#16232e" }}>{batchName}</strong> ·{" "}
            {topicName}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Search & Filters */}
      <Box
        sx={{
          p: "16px 24px",
          borderBottom: "1px solid #eef2f6",
          background: "#fbfdff",
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search by title or tag"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9db1bf" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 1.5,
            "& .MuiOutlinedInput-root": {
              background: "#fff",
              fontSize: "13.5px",
            },
          }}
        />
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
          {contentType === "assessments" &&
            FILTERS.map((filter) => (
              <Chip
                key={filter.key}
                label={filter.label}
                size="small"
                onClick={() => setActiveFilter(filter.key)}
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  background:
                    activeFilter === filter.key ? "#0172A6" : "#fff",
                  color: activeFilter === filter.key ? "#fff" : "#4a6070",
                  border: `1px solid ${
                    activeFilter === filter.key ? "#0172A6" : "#d5e0e9"
                  }`,
                  "&:hover": {
                    background:
                      activeFilter === filter.key ? "#015d8a" : "#f8fafc",
                  },
                }}
              />
            ))}
        </Box>
      </Box>

      {/* Library Items */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 6,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : libraryItems.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography sx={{ color: "#7b93a4", fontSize: "14px" }}>
              No items found
            </Typography>
          </Box>
        ) : (
          <>
            {libraryItems.map((item) => {
              const itemType = item.type || contentType.replace(/s$/, "");
              const typeConfig = TYPE_CONFIG[itemType] || TYPE_CONFIG.programming;
              const diffConfig = DIFFICULTY_CONFIG[item.difficulty];
              const assigned = isAssigned({ ...item, type: itemType });
              const selected = isSelected({ ...item, type: itemType });

              return (
                <Box
                  key={`${itemType}-${item.id}`}
                  onClick={() =>
                    handleToggleSelect({ ...item, type: itemType })
                  }
                  sx={{
                    p: "14px 24px",
                    borderBottom: "1px solid #f3f6f9",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    background: assigned
                      ? "#fbfdff"
                      : selected
                      ? "#f7fbfe"
                      : "#fff",
                    cursor: assigned ? "default" : "pointer",
                    opacity: assigned ? 0.6 : 1,
                    "&:hover": {
                      background: assigned ? "#fbfdff" : "#f8fafc",
                    },
                  }}
                >
                  {/* Checkbox */}
                  <Checkbox
                    checked={selected}
                    disabled={assigned}
                    size="small"
                    sx={{
                      p: 0,
                      color: assigned ? "#e4eaf0" : "#cfd9e2",
                      "&.Mui-checked": { color: "#0172A6" },
                    }}
                  />

                  {/* Type Icon */}
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      background: typeConfig.iconBg,
                      color: typeConfig.iconFg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontFamily: "'JetBrains Mono', monospace",
                      fontWeight: 700,
                      fontSize: "9.5px",
                      flexShrink: 0,
                    }}
                  >
                    {typeConfig.code}
                  </Box>

                  {/* Content Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: assigned ? "#7b93a4" : "#16232e",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item.title}
                      </Typography>
                      {diffConfig && (
                        <Chip
                          label={diffConfig.label}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "10.5px",
                            fontWeight: 700,
                            background: diffConfig.bg,
                            color: diffConfig.fg,
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "11.5px",
                        color: "#9db1bf",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.tags?.join(" · ") || item.description || ""}
                    </Typography>
                  </Box>

                  {/* Usage Info */}
                  <Box sx={{ textAlign: "right" }}>
                    <Typography sx={{ fontSize: "11.5px", color: "#7b93a4" }}>
                      {item.usage_count
                        ? `used in ${item.usage_count} topics`
                        : ""}
                    </Typography>
                    {assigned && (
                      <Typography
                        sx={{ fontSize: "11px", fontWeight: 600, color: "#7b93a4" }}
                      >
                        already assigned
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
            <Box
              sx={{
                p: "13px 24px",
                fontSize: "12.5px",
                color: "#7b93a4",
                background: "#fbfdff",
              }}
            >
              Showing {libraryItems.length} items · already-assigned items are
              greyed out
            </Box>
          </>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          borderTop: "1px solid #e4eaf0",
          p: "16px 24px",
          display: "flex",
          alignItems: "center",
          gap: 2,
          background: "#fff",
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: "13.5px", fontWeight: 700 }}>
            {selectedItems.length} selected
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "#7b93a4" }}>
            {totalScore > 0 ? `Max score ${totalScore} · ` : ""}
            adds to existing items
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderColor: "#d5e0e9",
            color: "#4a6070",
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={selectedItems.length === 0 || assigning}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            background: isBatch ? "#7a4fb5" : "#0172A6",
            "&:hover": { background: isBatch ? "#5c3a92" : "#015d8a" },
            "&:disabled": { background: "#d5e0e9" },
          }}
        >
          {assigning ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
            "Assign selected"
          )}
        </Button>
      </Box>
    </Drawer>
  );
};

export default LibraryPickerDrawer;
